import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Filter, Search, ChevronLeft, ChevronRight,
  ListTodo, CalendarDays, Sun, Download, FileText, FileSpreadsheet,
  Star, AlertTriangle, X, Loader2,
  LayoutGrid, BarChart3, Copy, Mail, Users, Settings
} from 'lucide-react';
import { DualNotificationBells } from '../components/NotificationBell';
import { TaskModal, CompleteModal, CreateTemplateModal } from '../components/Tasks/TaskModals';
import { API, getAuth, TASKS_PER_PAGE, PRIORITY_CONFIG, CATEGORY_CONFIG, STATUS_CONFIG, KANBAN_COLUMNS } from '../components/Tasks/taskConfigs';
import { SummaryCards } from '../components/Tasks/SummaryCards';
import { TaskCard } from '../components/Tasks/TaskCard';
import { KanbanBoard } from '../components/Tasks/KanbanBoard';
import { CalendarGrid } from '../components/Tasks/CalendarGrid';
import { AnalyticsDashboard } from '../components/Tasks/AnalyticsDashboard';
import { TemplatesView } from '../components/Tasks/TemplatesView';
import { TeamView } from '../components/Tasks/TeamView';
import { NotificationSettingsPanel } from '../components/Tasks/NotificationSettingsPanel';

const RECURRENCE_OPTIONS = [
  { value: 'none', label: 'No Repeat' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
];

export default function TaskBoard({ user }) {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [totalTasks, setTotalTasks] = useState(0);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('all');
  const [mainTab, setMainTab] = useState('team');
  const [calendarData, setCalendarData] = useState({});
  const [calendarMonth, setCalendarMonth] = useState(new Date().getMonth() + 1);
  const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [summary, setSummary] = useState({});
  const [users, setUsers] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [showCompleteModal, setShowCompleteModal] = useState(null);
  const [activeMenu, setActiveMenu] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [draggedTask, setDraggedTask] = useState(null);
  const [teamData, setTeamData] = useState(null);
  const [expandedUsers, setExpandedUsers] = useState({});
  const [page, setPage] = useState(1);
  const [showNotifSettings, setShowNotifSettings] = useState(false);
  // Team View filters
  const [teamSearch, setTeamSearch] = useState('');
  const [teamFilterStatuses, setTeamFilterStatuses] = useState([]);
  const [teamFilterPriorities, setTeamFilterPriorities] = useState([]);
  const [teamFilterCategories, setTeamFilterCategories] = useState([]);
  const [teamFilterUsers, setTeamFilterUsers] = useState([]);
  const [teamFilterRoles, setTeamFilterRoles] = useState([]);
  const [teamFilterDateFrom, setTeamFilterDateFrom] = useState(() => new Date().toISOString().split('T')[0]);
  const [teamFilterDateTo, setTeamFilterDateTo] = useState(() => new Date().toISOString().split('T')[0]);
  const [showTeamFilters, setShowTeamFilters] = useState(false);

  const isAdmin = user?.role?.toLowerCase() === 'admin' || user?.role_name?.toLowerCase()?.includes('admin') || user?.role_name?.toLowerCase()?.includes('manager') || user?.is_super_admin;

  const fetchTasks = useCallback(async (retryCount = 0) => {
    try {
      const params = new URLSearchParams({ view, page: String(page), per_page: String(TASKS_PER_PAGE) });
      if (search) params.set('search', search);
      if (filterStatus) params.set('status', filterStatus);
      if (filterPriority) params.set('priority', filterPriority);
      if (filterCategory) params.set('category', filterCategory);
      const { data } = await axios.get(`${API}/api/tasks?${params}`, getAuth());
      setTasks(data.tasks || []);
      setTotalTasks(data.total || 0);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/';
        return;
      }
      if (retryCount === 0) {
        setTimeout(() => fetchTasks(1), 800);
        return;
      }
      toast.error('Failed to load tasks. Pull down to refresh.');
    }
    setLoading(false);
  }, [view, search, filterStatus, filterPriority, filterCategory, page]);

  const _handle401 = (err) => {
    if (err?.response?.status === 401) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/';
      return true;
    }
    return false;
  };

  const fetchSummary = async () => {
    try { const { data } = await axios.get(`${API}/api/tasks/my-summary`, getAuth()); setSummary(data); } catch (e) { _handle401(e); }
  };
  const fetchUsers = async () => {
    try { const { data } = await axios.get(`${API}/api/tasks/users`, getAuth()); setUsers(data.users || []); } catch (e) { _handle401(e); }
  };
  const fetchCalendar = async () => {
    try {
      const { data } = await axios.get(`${API}/api/tasks/calendar?month=${calendarMonth}&year=${calendarYear}`, getAuth());
      setCalendarData(data.tasks_by_date || {});
    } catch (e) { _handle401(e); }
  };
  const fetchAnalytics = async () => {
    try { const { data } = await axios.get(`${API}/api/tasks/analytics`, getAuth()); setAnalytics(data); } catch (e) { _handle401(e); }
  };
  const fetchTemplates = async () => {
    try { const { data } = await axios.get(`${API}/api/tasks/templates`, getAuth()); setTemplates(data.templates || []); } catch (e) { _handle401(e); }
  };
  const fetchTeamView = async () => {
    try { const { data } = await axios.get(`${API}/api/tasks/team-view`, getAuth()); setTeamData(data.role_groups || {}); } catch (e) { _handle401(e); }
  };

  useEffect(() => { fetchTasks(); fetchSummary(); fetchUsers(); }, [fetchTasks]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (mainTab === 'calendar') fetchCalendar(); }, [mainTab, calendarMonth, calendarYear]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (mainTab === 'analytics') fetchAnalytics(); }, [mainTab]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (mainTab === 'templates') fetchTemplates(); }, [mainTab]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (mainTab === 'team' && isAdmin) fetchTeamView(); }, [mainTab, isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (mainTab === 'team' && !isAdmin) setMainTab('list'); }, [mainTab, isAdmin]);

  // Reset page to 1 when filters change
  useEffect(() => { setPage(1); }, [view, search, filterStatus, filterPriority, filterCategory]);

  const refreshAll = useCallback(() => {
    fetchTasks();
    fetchSummary();
    if (isAdmin) fetchTeamView();
    if (mainTab === 'calendar') fetchCalendar();
    if (mainTab === 'analytics') fetchAnalytics();
  }, [mainTab, isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-poll every 30s
  useEffect(() => {
    if (showCreateModal || showCompleteModal || showTemplateModal) return;
    const interval = setInterval(() => {
      fetchTasks();
      fetchSummary();
      if (isAdmin && mainTab === 'team') fetchTeamView();
    }, 30000);
    return () => clearInterval(interval);
  }, [mainTab, isAdmin, showCreateModal, showCompleteModal, showTemplateModal]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleQuickStatus = async (taskId, newStatus) => {
    if (['completed', 'partial', 'not_completed'].includes(newStatus)) {
      setShowCompleteModal(tasks.find(t => t.id === taskId));
      return;
    }
    try {
      await axios.put(`${API}/api/tasks/${taskId}`, { status: newStatus }, getAuth());
      fetchTasks(); fetchSummary();
    } catch { toast.error('Failed to update'); }
  };

  const handleDelete = async (taskId) => {
    if (!window.confirm('Delete this task?')) return;
    try {
      await axios.delete(`${API}/api/tasks/${taskId}`, getAuth());
      toast.success('Task deleted'); fetchTasks(); fetchSummary();
    } catch { toast.error('Failed to delete'); }
  };

  const handleExport = async (format) => {
    try {
      const resp = await axios.get(`${API}/api/tasks/export/${format}`, { ...getAuth(), responseType: 'blob' });
      const url = window.URL.createObjectURL(resp.data);
      const a = document.createElement('a'); a.href = url;
      a.download = `tasks_export.${format === 'excel' ? 'xlsx' : 'docx'}`;
      a.click(); toast.success(`Exported as ${format.toUpperCase()}`);
    } catch { toast.error('Export failed'); }
  };

  const handleKanbanDrop = async (taskId, newStatus) => {
    try {
      await axios.put(`${API}/api/tasks/${taskId}/status`, { status: newStatus }, getAuth());
      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: newStatus } : t));
      refreshAll();
    } catch { toast.error('Failed to move task'); fetchTasks(); }
  };

  const handleCreateFromTemplate = async (template) => {
    try {
      await axios.post(`${API}/api/tasks/from-template`, { template_id: template.id }, getAuth());
      toast.success(`Task created from "${template.name}"`);
      refreshAll();
      setMainTab('list');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const [kanbanPages, setKanbanPages] = useState({});
  const taskCardProps = {
    mainTab, setDraggedTask: (t) => setDraggedTask(t),
    handleQuickStatus, activeMenu, setActiveMenu,
    setEditingTask, setShowCreateModal, setShowCompleteModal, handleDelete,
  };

  const totalPages = Math.ceil(totalTasks / TASKS_PER_PAGE);

  const MAIN_TABS = [
    { id: 'list', label: 'List', icon: ListTodo },
    { id: 'kanban', label: 'Kanban', icon: LayoutGrid },
    { id: 'calendar', label: 'Calendar', icon: CalendarDays },
    ...(isAdmin ? [{ id: 'team', label: 'Team View', icon: Users }] : []),
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
    { id: 'templates', label: 'Templates', icon: Copy },
  ];

  return (
    <div className="min-h-screen page-wash-tasks">
      {/* Header — premium frosted glass with amber accent (Phase 4) */}
      <div className="bg-white/75 backdrop-blur-xl text-slate-900 px-4 sm:px-6 py-3 flex items-center justify-between sticky top-0 z-30 border-b border-amber-100/70 shadow-[0_1px_2px_rgba(0,0,0,0.04)]" data-testid="task-board-header">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-slate-500 hover:text-slate-900 transition-colors" data-testid="task-back-btn"><ChevronLeft className="w-5 h-5" /></button>
          <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg shadow-[0_4px_12px_rgba(217,119,6,0.30)]" style={{ background: 'linear-gradient(135deg, #D97706 0%, #EA580C 60%, #DC2626 100%)' }}>
            <ListTodo className="w-4 h-4 text-white" />
          </span>
          <h1 className="text-base sm:text-lg font-semibold tracking-tight" style={{ fontFamily: 'Geist, Inter, sans-serif' }}>Tasks &amp; To-Do</h1>
        </div>
        <div className="flex items-center gap-2">
          <DualNotificationBells />
          <button onClick={() => setShowNotifSettings(true)} className="p-2 rounded-xl hover:bg-amber-50 transition-colors" title="Notification Settings" data-testid="notif-settings-btn">
            <Settings className="w-4 h-4" />
          </button>
          <a href="/tasks/email-digest" className="p-2 rounded-xl hover:bg-amber-50 transition-colors" title="Email Digest Settings" data-testid="email-digest-link">
            <Mail className="w-4 h-4" />
          </a>
          <div className="relative group">
            <button className="p-2 rounded-xl hover:bg-amber-50 transition-colors" data-testid="export-btn"><Download className="w-4 h-4" /></button>
            <div className="absolute right-0 top-9 bg-white/95 backdrop-blur-xl text-gray-700 rounded-xl shadow-[0_10px_30px_rgba(0,0,0,0.10)] border border-slate-200/70 py-1 w-44 hidden group-hover:block">
              <button onClick={() => handleExport('excel')} className="w-full px-3 py-1.5 text-left text-xs hover:bg-amber-50 flex items-center gap-2" data-testid="export-excel"><FileSpreadsheet className="w-3 h-3" /> Export to Excel</button>
              <button onClick={() => handleExport('word')} className="w-full px-3 py-1.5 text-left text-xs hover:bg-amber-50 flex items-center gap-2" data-testid="export-word"><FileText className="w-3 h-3" /> Export to Word</button>
            </div>
          </div>
          <button onClick={() => { setEditingTask(null); setShowCreateModal(true); }} className="flex items-center gap-1.5 px-3 py-1.5 text-white rounded-xl text-sm font-medium shadow-[0_4px_12px_rgba(217,119,6,0.30)] hover:shadow-[0_8px_20px_rgba(217,119,6,0.40)] transition-all duration-180 ease-out hover:scale-[1.02] active:scale-95" style={{ background: 'linear-gradient(135deg, #EA580C 0%, #DC2626 60%, #BE185D 100%)' }} data-testid="create-task-btn">
            <Plus className="w-4 h-4" /> New Task
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5">
        <SummaryCards summary={summary} setView={setView} setMainTab={setMainTab} setFilterStatus={setFilterStatus} />

        {/* Main Tabs + Pagination (top-right) */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-4">
          <div className="flex gap-1 bg-white/70 backdrop-blur-xl rounded-xl border border-slate-200/70 p-1 flex-shrink-0 shadow-sm" data-testid="main-tabs">
            {MAIN_TABS.map(tab => (
              <button key={tab.id} onClick={() => setMainTab(tab.id)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-180 ease-out ${mainTab === tab.id ? 'text-white shadow-[0_4px_10px_rgba(217,119,6,0.30)]' : 'text-gray-600 hover:bg-amber-50/70'}`} style={mainTab === tab.id ? { background: 'linear-gradient(135deg, #D97706 0%, #EA580C 100%)' } : {}} data-testid={`main-tab-${tab.id}`}>
                <tab.icon className="w-3.5 h-3.5" /> {tab.label}
              </button>
            ))}
          </div>
          {/* Server-Side Pagination - Top Right */}
          {mainTab === 'list' && totalPages > 0 && (
            <div className="flex items-center gap-2 ml-auto" data-testid="pagination-top">
              <span className="text-xs text-gray-500">
                {Math.min((page - 1) * TASKS_PER_PAGE + 1, totalTasks)}-{Math.min(page * TASKS_PER_PAGE, totalTasks)} of {totalTasks}
              </span>
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="p-1.5 bg-white border rounded-lg disabled:opacity-30 hover:bg-gray-50" data-testid="pagination-prev">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-xs font-medium text-gray-700 min-w-[40px] text-center">{page}/{totalPages}</span>
              <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="p-1.5 bg-white border rounded-lg disabled:opacity-30 hover:bg-gray-50" data-testid="pagination-next">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        {/* Sub-filters for list/kanban views */}
        {(mainTab === 'list' || mainTab === 'kanban') && (
          <>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-4">
              <div className="flex gap-1 bg-white/70 backdrop-blur-xl rounded-xl border border-slate-200/70 p-1 shadow-sm">
                {[
                  { v: 'all', l: 'All', icon: ListTodo },
                  { v: 'my_day', l: 'My Day', icon: Sun },
                  { v: 'important', l: 'Important', icon: Star },
                  { v: 'overdue', l: 'Overdue', icon: AlertTriangle },
                ].map(tab => (
                  <button key={tab.v} onClick={() => { setView(tab.v); setFilterStatus(''); setFilterPriority(''); setFilterCategory(''); }} className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-180 ease-out ${view === tab.v ? 'text-white shadow-[0_4px_10px_rgba(217,119,6,0.30)]' : 'text-gray-600 hover:bg-amber-50/70'}`} style={view === tab.v ? { background: 'linear-gradient(135deg, #D97706 0%, #EA580C 100%)' } : {}} data-testid={`view-tab-${tab.v}`}>
                    <tab.icon className="w-3 h-3" /> {tab.l}
                  </button>
                ))}
              </div>
              <div className="flex-1 relative">
                <Search className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search tasks..." className="w-full pl-9 pr-3 py-2 border border-slate-200/70 rounded-xl text-sm bg-white/70 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-amber-400/40 focus:border-amber-400/60 focus:bg-white transition-all duration-240" data-testid="task-search" />
              </div>
              <button onClick={() => setShowFilters(!showFilters)} className={`flex items-center gap-1 px-3 py-2 border rounded-xl text-xs font-medium transition-all duration-180 ease-out ${showFilters ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-white/70 backdrop-blur-sm text-gray-600 border-slate-200/70 hover:bg-amber-50/70'}`} data-testid="toggle-filters-btn">
                <Filter className="w-3 h-3" /> Filters
              </button>
            </div>
            {showFilters && (
              <div className="flex flex-wrap gap-2 mb-4 p-3 bg-white rounded-xl border">
                <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="px-2 py-1 border rounded-lg text-xs" data-testid="filter-status">
                  <option value="">All Status</option>
                  {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
                <select value={filterPriority} onChange={e => setFilterPriority(e.target.value)} className="px-2 py-1 border rounded-lg text-xs" data-testid="filter-priority">
                  <option value="">All Priority</option>
                  {Object.entries(PRIORITY_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
                <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)} className="px-2 py-1 border rounded-lg text-xs" data-testid="filter-category">
                  <option value="">All Category</option>
                  {Object.entries(CATEGORY_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
                </select>
                <button onClick={() => { setFilterStatus(''); setFilterPriority(''); setFilterCategory(''); setSearch(''); }} className="px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded-lg">Clear All</button>
              </div>
            )}
          </>
        )}

        {/* Content Area */}
        {mainTab === 'list' && (
          <div className="space-y-1.5">
            {loading ? (
              <div className="text-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400 mx-auto" /></div>
            ) : tasks.length === 0 ? (
              <div className="empty-state" style={{ borderColor: 'rgba(217,119,6,0.20)' }}>
                <div className="empty-state-icon" style={{ background: 'linear-gradient(135deg, rgba(217,119,6,0.14) 0%, rgba(234,88,12,0.10) 100%)' }}>
                  <ListTodo className="w-8 h-8 text-amber-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-800 tracking-tight" style={{ fontFamily: 'Geist, Inter, sans-serif' }}>No tasks here yet</h3>
                <p className="text-sm text-slate-500 mt-1.5 max-w-md">Capture what's on your mind. Add deadlines, priorities, and assignees — your team will never miss a beat.</p>
                <button onClick={() => { setEditingTask(null); setShowCreateModal(true); }} className="mt-5 px-5 py-2.5 text-white text-sm font-medium rounded-xl shadow-[0_4px_12px_rgba(217,119,6,0.30)] hover:shadow-[0_8px_20px_rgba(217,119,6,0.40)] transition-all duration-180 ease-out hover:scale-[1.02] active:scale-95" style={{ background: 'linear-gradient(135deg, #EA580C 0%, #DC2626 100%)' }} data-testid="create-first-task">Create First Task</button>
              </div>
            ) : (
              <>
                {tasks.map((t, idx) => <TaskCard key={t.id} task={t} serialNum={(page - 1) * TASKS_PER_PAGE + idx + 1} {...taskCardProps} />)}
              </>
            )}
          </div>
        )}

        {mainTab === 'kanban' && <KanbanBoard tasks={tasks} kanbanPages={kanbanPages} setKanbanPages={setKanbanPages} draggedTask={draggedTask} handleKanbanDrop={handleKanbanDrop} taskCardProps={taskCardProps} />}
        {mainTab === 'calendar' && <CalendarGrid calendarYear={calendarYear} calendarMonth={calendarMonth} setCalendarYear={setCalendarYear} setCalendarMonth={setCalendarMonth} calendarData={calendarData} />}
        {mainTab === 'team' && isAdmin && <TeamView teamData={teamData} teamSearch={teamSearch} setTeamSearch={setTeamSearch} showTeamFilters={showTeamFilters} setShowTeamFilters={setShowTeamFilters} teamFilterRoles={teamFilterRoles} setTeamFilterRoles={setTeamFilterRoles} teamFilterUsers={teamFilterUsers} setTeamFilterUsers={setTeamFilterUsers} teamFilterStatuses={teamFilterStatuses} setTeamFilterStatuses={setTeamFilterStatuses} teamFilterPriorities={teamFilterPriorities} setTeamFilterPriorities={setTeamFilterPriorities} teamFilterCategories={teamFilterCategories} setTeamFilterCategories={setTeamFilterCategories} teamFilterDateFrom={teamFilterDateFrom} setTeamFilterDateFrom={setTeamFilterDateFrom} teamFilterDateTo={teamFilterDateTo} setTeamFilterDateTo={setTeamFilterDateTo} expandedUsers={expandedUsers} setExpandedUsers={setExpandedUsers} taskCardProps={taskCardProps} />}
        {mainTab === 'analytics' && <AnalyticsDashboard analytics={analytics} isAdmin={isAdmin} />}
        {mainTab === 'templates' && <TemplatesView templates={templates} setShowTemplateModal={setShowTemplateModal} handleCreateFromTemplate={handleCreateFromTemplate} />}
      </div>

      {/* Modals */}
      {showCreateModal && <TaskModal editingTask={editingTask} setEditingTask={setEditingTask} setShowCreateModal={setShowCreateModal} templates={templates} users={users} user={user} refreshAll={refreshAll} />}
      {showCompleteModal && <CompleteModal showCompleteModal={showCompleteModal} setShowCompleteModal={setShowCompleteModal} setEditingTask={setEditingTask} setShowCreateModal={setShowCreateModal} refreshAll={refreshAll} />}
      {showTemplateModal && <CreateTemplateModal setShowTemplateModal={setShowTemplateModal} fetchTemplates={fetchTemplates} />}
      {showNotifSettings && <NotificationSettingsPanel user={user} onClose={() => setShowNotifSettings(false)} />}
    </div>
  );
}
